/**
 * Third-party provider status, normalised from each provider's public status page
 * (Atlassian Statuspage `/api/v2/*.json` or Better Stack `/index.json`). Both
 * are public, CORS-enabled and need no API key, so they're fetched from the browser.
 *
 * Ported from skynet-web (core/models/provider-status.model.ts). Keep the parsing in
 * step with Skynet when either changes.
 */

/** Ordered from best to worst — see PROVIDER_LEVEL_RANK. */
export type ProviderLevel =
    | 'operational'
    | 'maintenance'
    | 'degraded'
    | 'partial_outage'
    | 'major_outage'
    | 'unknown';

export const PROVIDER_LEVEL_RANK: Record<ProviderLevel, number> = {
    unknown: -1,
    operational: 0,
    maintenance: 1,
    degraded: 2,
    partial_outage: 3,
    major_outage: 4,
};

export type ProviderSource = 'statuspage' | 'betterstack';

export type StatusProviderId = 'frisbii' | 'stripe' | 'surfboard' | 'swish' | 'vippsmobilepay';

export interface ProviderConfig {
    id: StatusProviderId;
    name: string;
    source: ProviderSource;
    /** Origin the JSON API is read from. */
    apiBaseUrl: string;
    /** Public status page opened from the Status page. */
    pageUrl: string;
    /** Events whose title matches are left out (e.g. noise forwarded from other providers). */
    hideEventsMatching?: RegExp;
    /**
     * Statuspage only: follow just these component groups (e.g. countries). Status, affected
     * components and events are then based on those groups instead of the whole page.
     */
    componentGroups?: string[];
}

export type ProviderEventKind = 'incident' | 'maintenance';

/** Where an event is in its lifecycle — drives the badge and list ordering. */
export type ProviderEventPhase = 'active' | 'scheduled' | 'resolved';

export interface ProviderEvent {
    id: string;
    providerId: StatusProviderId;
    providerName: string;
    kind: ProviderEventKind;
    phase: ProviderEventPhase;
    /** Severity while the event is active, used for the dot colour. */
    level: ProviderLevel;
    title: string;
    /** Latest update text from the provider, plain text. */
    message: string;
    /** Planned/actual start. For incidents without one, the creation time. */
    startsAt: Date | null;
    /** Planned/actual end; null while ongoing or unknown. */
    endsAt: Date | null;
    updatedAt: Date | null;
    /** Names of affected components (e.g. "MP Online API"). */
    affected: string[];
    link: string;
}

export interface ProviderComponentStatus {
    name: string;
    level: ProviderLevel;
}

export interface ProviderStatus {
    id: StatusProviderId;
    name: string;
    pageUrl: string;
    level: ProviderLevel;
    /** The provider's own headline, e.g. "All Systems Operational". */
    description: string;
    /** Only components that are not operational. */
    affectedComponents: ProviderComponentStatus[];
    events: ProviderEvent[];
    /** Set when the status page could not be fetched or parsed. */
    error: string | null;
    /** True until the first response for this provider has arrived. */
    loading: boolean;
}

/**
 * Every provider a unit can be shown. Which ones a unit actually gets is decided per unit by
 * resolveStatusProviders() (services/unit-status-providers.service.ts). Skynet also follows
 * Nets and Mailgun; those are internal and never shown to customers.
 */
export const STATUS_PROVIDERS: Record<StatusProviderId, ProviderConfig> = {
    frisbii: {
        id: 'frisbii',
        name: 'Frisbii',
        source: 'statuspage',
        apiBaseUrl: 'https://status.frisbii.com',
        pageUrl: 'https://status.frisbii.com',
    },
    stripe: {
        id: 'stripe',
        name: 'Stripe',
        source: 'statuspage',
        apiBaseUrl: 'https://www.stripestatus.com',
        pageUrl: 'https://status.stripe.com',
        // Stripe republishes every payment-method partner's maintenance (TWINT, FPX, Swish banks …)
        // as "Scheduled maintenance for X". Hide those; Swish/Vipps MobilePay have their own entries.
        hideEventsMatching: /^Scheduled maintenance for /i,
    },
    surfboard: {
        id: 'surfboard',
        name: 'Surfboard',
        source: 'betterstack',
        apiBaseUrl: 'https://status.surfboardpayments.com',
        pageUrl: 'https://status.surfboardpayments.com',
    },
    swish: {
        id: 'swish',
        name: 'Swish',
        source: 'statuspage',
        apiBaseUrl: 'https://status.swish.nu',
        pageUrl: 'https://status.swish.nu',
    },
    // One page for the whole company, grouped by country. Scoped to the unit's country by
    // vippsMobilePayFor(); this unscoped entry is the fallback for any other country.
    vippsmobilepay: {
        id: 'vippsmobilepay',
        name: 'Vipps MobilePay',
        source: 'statuspage',
        apiBaseUrl: 'https://status.vippsmobilepay.com',
        pageUrl: 'https://status.vippsmobilepay.com',
    },
};

/** Vipps MobilePay status page group and consumer brand per sales location country code. */
const VIPPS_MOBILEPAY_COUNTRIES: Record<string, { group: string; name: string }> = {
    NO: { group: 'Norway', name: 'Vipps (Norway)' },
    DK: { group: 'Denmark', name: 'MobilePay (Denmark)' },
    FI: { group: 'Finland', name: 'MobilePay (Finland)' },
};

/** The Vipps MobilePay entry scoped to the unit's country; the whole page when the country is unknown. */
export function vippsMobilePayFor(countryCode: string | null | undefined): ProviderConfig {
    const country = VIPPS_MOBILEPAY_COUNTRIES[(countryCode || '').trim().toUpperCase()];
    const base = STATUS_PROVIDERS.vippsmobilepay;
    return country ? { ...base, name: country.name, componentGroups: [country.group] } : base;
}

/** Display order on the Status page. */
export const STATUS_PROVIDER_ORDER: StatusProviderId[] = ['frisbii', 'stripe', 'surfboard', 'swish', 'vippsmobilepay'];
